import React, { useState, useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { RiArrowRightSLine } from "react-icons/ri";
import { putRequest } from "../../helpers/api.helper";
import { DropdownOption } from "../../types";
import { heightOptions } from "../../utils/constants";

const Step2Edit = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const { profile } = location.state || {};

  /** ---------- State Management ---------- **/
  const [modalData, setModalData] = useState<any>(null);
  const [searchTerm, setSearchTerm] = useState("");

  // Initialize form data with profile data
  const [formData, setFormData] = useState({
    dateOfBirth: profile?.dateOfBirth
      ? new Date(profile.dateOfBirth).toISOString().split("T")[0]
      : "",
    time: profile?.time || "",
    maritalStatus: profile?.maritalStatus || "",
    maleKids: profile?.maleKids || "",
    femaleKids: profile?.femaleKids || "",
    hasRelatives: profile?.hasRelatives || "",
  });

  // Initialize dropdown selections with profile data
  const [selectedOptions, setSelectedOptions] = useState<any>({
    height: profile?.height
      ? {
          value: String(profile.height),
          label: `${profile.height} ft`,
        }
      : { value: "4.0", label: "4'0\" (122 cm)" },
    weight: profile?.weight
      ? { value: profile.weight, label: `${profile.weight} kg` }
      : { value: "30", label: "30 kg" },
    Physical_State: profile?.physicalState
      ? {
          value: profile.physicalState,
          label: getPhysicalStateLabel(profile.physicalState),
        }
      : { value: "", label: "Select" },
    Eating_Habits: profile?.eatingHabits
      ? {
          value: profile.eatingHabits,
          label: getEatingHabitsLabel(profile.eatingHabits),
        }
      : { value: "", label: "Select" },
    Smoking_Habits: profile?.smokingHabits
      ? {
          value: profile.smokingHabits,
          label: getHabitsLabel(profile.smokingHabits),
        }
      : { value: "", label: "Select" },
    Drinking_Habits: profile?.drinkingHabits
      ? {
          value: profile.drinkingHabits,
          label: getHabitsLabel(profile.drinkingHabits),
        }
      : { value: "", label: "Select" },
  });

  // Helper functions for label conversion
  function getPhysicalStateLabel(value: any) {
    const labels: any = {
      normal: "Normal",
      slim: "Slim",
      challenged: "Challenged",
    };
    return labels[value] || "Select";
  }

  function getEatingHabitsLabel(value: any) {
    const labels: any = {
      vegetarian: "Vegetarian",
      nonVegetarian: "Non-Vegetarian",
      eggetarian: "Eggetarian",
    };
    return labels[value] || "Select";
  }

  function getHabitsLabel(value: any) {
    const labels: any = {
      no: "No",
      occasionally: "Occasional",
      yes: "Yes",
    };
    return labels[value] || "Select";
  }

  /** ---------- Dropdown Options ---------- **/
  const dropdownOptions: any = {
    height: {
      type: "single",
      options: heightOptions.map((option) => ({
        value: String(option.value),
        label: option.label,
      })),
    },
    weight: {
      type: "single",
      options: Array.from({ length: 171 }, (_, i) => {
        const value = 30 + i;
        return { value: value.toString(), label: `${value} kg` };
      }),
    },
    Physical_State: {
      type: "single",
      options: [
        { value: "", label: "Select" },
        { value: "normal", label: "Normal" },
        { value: "slim", label: "Slim" },
        { value: "challenged", label: "Challenged" },
      ],
    },
    Eating_Habits: {
      type: "single",
      options: [
        { value: "", label: "Select" },
        { value: "vegetarian", label: "Vegetarian" },
        { value: "nonVegetarian", label: "Non-Vegetarian" },
        { value: "eggetarian", label: "Eggetarian" },
      ],
    },
    Smoking_Habits: {
      type: "single",
      options: [
        { value: "", label: "Select" },
        { value: "no", label: "No" },
        { value: "occasionally", label: "Occasional" },
        { value: "yes", label: "Yes" },
      ],
    },
    Drinking_Habits: {
      type: "single",
      options: [
        { value: "", label: "Select" },
        { value: "no", label: "No" },
        { value: "occasionally", label: "Occasional" },
        { value: "yes", label: "Yes" },
      ],
    },
  };

  /** ---------- Effects ---------- **/
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (modalData) {
      setSearchTerm("");
    }
  }, [modalData]);

  /** ---------- Handlers ---------- **/
  const openModal = (key: any) => {
    setModalData({
      key,
      type: dropdownOptions[key].type,
      options: dropdownOptions[key].options,
    });
  };

  const closeModal = () => {
    setModalData(null);
    setSearchTerm("");
  };

  const handleSingleSelect = (option: DropdownOption) => {
    setSelectedOptions((prev: any) => ({
      ...prev,
      [modalData.key]: option,
    }));
    closeModal();
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const formattedOptions = {
      height: selectedOptions.height.value,
      weight: selectedOptions.weight.value,
      physicalState: selectedOptions.Physical_State.value,
      eatingHabits: selectedOptions.Eating_Habits.value,
      smokingHabits: selectedOptions.Smoking_Habits.value,
      drinkingHabits: selectedOptions.Drinking_Habits.value,
    };

    const formDataToSend = {
      dateOfBirth: formData.dateOfBirth,
      time: formData.time,
      maritalStatus: formData.maritalStatus,
      maleKids: formData.maleKids,
      femaleKids: formData.femaleKids,
      hasRelatives: formData.hasRelatives,
      ...formattedOptions,
      step1: 1,
      step2: 1,
    };

    try {
      const response = await putRequest(
        `/bureau/profile/${profile._id}`,
        formDataToSend
      );
      if (response.success) {
        alert("Details updated successfully!");
        navigate(-1);
      } else {
        alert("Failed to update details.");
      }
    } catch (error) {
      console.error("Error updating details:", error);
      alert("An error occurred while updating details.");
    }
  };

  const handleBackClick = () => {
    navigate(-1);
  };

  /** ---------- Helper Functions ---------- **/
  const getDisplayValue = (key: keyof typeof selectedOptions) => {
    const value = selectedOptions[key];
    //@ts-ignore
    return value?.label || `Select ${key.replace(/_/g, " ")}`;
  };

  /** ---------- Reusable Modal Component ---------- **/
  const Modal = () => {
    if (!modalData) return null;

    const filteredOptions = modalData.options.filter((option: DropdownOption) =>
      option.label.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
        <div className="bg-white p-6 rounded-lg max-w-md w-full shadow-lg m-5">
          <h2 className="text-lg font-bold mb-4 capitalize text-center">
            Select {modalData.key.replace(/_/g, " ")}
          </h2>

          {/* Search Bar */}
          <input
            type="text"
            autoFocus
            className="w-full px-4 py-2 mb-4 border border-gray-300 rounded focus:outline-none focus:ring focus:ring-blue-200"
            placeholder={`Search ${modalData.key.replace(/_/g, " ")}`}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />

          {/* Options List */}
          <div className="h-64 overflow-y-auto border-t border-b border-gray-200">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option: DropdownOption, index: number) => (
                <React.Fragment key={option.value}>
                  <button
                    className="block w-full text-left px-4 py-2 hover:bg-gray-100 transition"
                    onClick={() => handleSingleSelect(option)}
                  >
                    {option.label}
                  </button>
                  {index < filteredOptions.length - 1 && (
                    <hr className="border-gray-200" />
                  )}
                </React.Fragment>
              ))
            ) : (
              <div className="text-center text-gray-500 py-4">
                No options found
              </div>
            )}
          </div>

          {/* Close Button */}
          <button
            className="mt-4 w-full px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 transition"
            onClick={closeModal}
          >
            Continue
          </button>
        </div>
      </div>
    );
  };

  /** ---------- Render ---------- **/
  return (
    <div className="profile-edit-page flex justify-center items-center min-h-screen bg-gradient-to-r from-blue-50 to-red-100">
      <div className="w-full max-w-lg bg-white shadow-lg rounded-lg">
        {/* Header */}
        <div className="w-full z-50 bg-red-600 fixed top-0 mb-12 p-2 px-3 flex items-center justify-center">
          <div className="flex items-center gap-2">
            <button className="flex items-center" onClick={handleBackClick}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6 text-white"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>
            <h1 className="text-2xl font-bold text-center text-white py-3">
              Step 2 -
            </h1>
            <h5 className="font-semibold text-white">Personal Details</h5>
          </div>
        </div>

        {/* Form */}
        <form className="space-y-6 px-3 mt-20 py-4" onSubmit={handleSubmit}>
          {/* Date of Birth */}
          <div>
            <label className="block text-red-500 font-medium mb-2">
              Date of Birth
            </label>
            <input
              placeholder="Enter Date of Birth"
              type="date"
              name="dateOfBirth"
              value={formData.dateOfBirth}
              onChange={handleChange}
              className="w-full p-4 border border-[#c4d4e2] rounded-md focus:outline-none focus:ring-2 focus:ring-[#c4d4e2] bg-white"
            />
          </div>

          {/* Birth Time */}
          <div>
            <label className="block text-red-500 font-medium mb-2">
              Birth Time
            </label>
            <input
              placeholder="Enter Birth Time"
              type="time"
              name="time"
              value={formData.time}
              onChange={handleChange}
              className="w-full p-4 border border-[#c4d4e2] rounded-md focus:outline-none focus:ring-2 focus:ring-[#c4d4e2] bg-white"
            />
          </div>

          {/* Marital Status */}
          <div>
            <label className="block text-red-500 font-medium mb-2">
              Marital Status
            </label>
            <select
              name="maritalStatus"
              value={formData.maritalStatus}
              onChange={handleChange}
              className="w-full p-4 border border-[#c4d4e2] rounded-md focus:outline-none focus:ring-2 focus:ring-[#c4d4e2] bg-white"
            >
              <option value="">Select Marital Status</option>
              <option value="neverMarried">Never Married</option>
              <option value="awaitingDivorce">Awaiting Divorce</option>
              <option value="divorced">Divorced</option>
              <option value="widow">Widow</option>
            </select>
          </div>

          {/* Conditional Fields for Married Status */}
          {formData.maritalStatus !== "neverMarried" &&
            formData.maritalStatus !== "" && (
              <>
                <div>
                  <label className="block text-red-500 font-medium mb-2">
                    Male Kids
                  </label>
                  <select
                    name="maleKids"
                    value={formData.maleKids}
                    onChange={handleChange}
                    className="w-full p-4 border border-[#c4d4e2] rounded-md focus:outline-none focus:ring-2 focus:ring-[#c4d4e2] bg-white"
                  >
                    <option value="">Select</option>
                    {[...Array(11)].map((_, i) => (
                      <option key={i} value={i}>
                        {i}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-red-500 font-medium mb-2">
                    Female Kids
                  </label>
                  <select
                    name="femaleKids"
                    value={formData.femaleKids}
                    onChange={handleChange}
                    className="w-full p-4 border border-[#c4d4e2] rounded-md focus:outline-none focus:ring-2 focus:ring-[#c4d4e2] bg-white"
                  >
                    <option value="">Select</option>
                    {[...Array(11)].map((_, i) => (
                      <option key={i} value={i}>
                        {i}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-red-500 font-medium mb-2">
                    Are the born children with you?
                  </label>
                  <select
                    name="hasRelatives"
                    value={formData.hasRelatives}
                    onChange={handleChange}
                    className="w-full p-4 border border-[#c4d4e2] rounded-md focus:outline-none focus:ring-2 focus:ring-[#c4d4e2] bg-white"
                  >
                    <option value="">Select</option>
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                  </select>
                </div>
              </>
            )}

          {/* Modal Fields */}
          {Object.keys(dropdownOptions).map((key) => (
            <div key={key}>
              <label className="block text-red-500 font-medium mb-2 capitalize">
                {key.replace(/_/g, " ")}
              </label>
              <div
                className="flex flex-col w-full border px-3 border-gray-300 py-3 cursor-pointer rounded-lg hover:shadow-lg transition-all"
                onClick={() => openModal(key)}
              >
                <div className="flex items-center justify-between">
                  <span className="text-gray-500 text-md font-medium">
                    {getDisplayValue(key)}
                  </span>
                  <RiArrowRightSLine className="text-3xl text-gray-500 ml-3" />
                </div>
              </div>
            </div>
          ))}

          {/* Submit Button */}
          <button
            type="submit"
            className="w-full bg-red-700 hover:bg-red-500 text-white font-bold py-3 rounded-lg transition-colors"
          >
            Update Personal Details
          </button>
        </form>

        {/* Reusable Modal */}
        <Modal />
      </div>
    </div>
  );
};

export default Step2Edit;
